import { useFeatureFlag } from "@app/shared/hooks/use-feature-flag";
import { URLS } from "@core/shared/utils";
import { cn } from "@design-system/lib/utils";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@design-system/ui/dropdown-menu";
import { trans, plainTrans } from "@mongez/localization";
import { Link, useLocation } from "@remix-run/react";
import { ChevronDown } from "lucide-react";
import {
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  type ElementType,
} from "react";
import { useHomeNavigationLinks } from "../../../hooks/use-home-navigation-links.hook";
import { useNavigationLinks } from "../../../hooks/use-navigation-links";

const useIsoLayoutEffect =
  typeof window === "undefined" ? useEffect : useLayoutEffect;

type NavItem = {
  to: string;
  label: string;
  icon: ElementType;
  /** Home must match exactly, everything else by prefix. */
  exact?: boolean;
};

/** Lower rank = stays visible longer. Unknown links go after the known ones. */
function getRank(item: NavItem, feedUrl: string) {
  const order = [
    URLS.home,
    URLS.lessons.list,
    URLS.assessments.list,
    URLS.trainingGround,
    URLS.league,
    URLS.communityGroups.list,
    URLS.leaderboard.topAssessments,
    feedUrl,
    URLS.offers.list,
  ];
  const index = order.indexOf(item.to);

  return index === -1 ? order.length : index;
}

function isItemActive(item: NavItem, pathname: string) {
  return item.exact ? pathname === item.to : pathname.startsWith(item.to);
}

function getItemClassName(to: string, isActive: boolean) {
  const isLeaderboard = to === URLS.leaderboard.topAssessments;
  const isChallenges = to === URLS.challenges.list;
  const isTrainingGround = to === URLS.trainingGround;
  const isAccent = isLeaderboard || isChallenges || isTrainingGround;

  return cn(
    "group relative flex shrink-0 items-center gap-2 overflow-hidden whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-bold transition-all duration-200",
    isLeaderboard &&
      "border border-yellow-200/50 bg-gradient-to-r from-yellow-50 to-orange-50 dark:border-yellow-700/50 dark:from-yellow-900/20 dark:to-orange-900/20",
    isChallenges &&
      "border border-fuchsia-200/50 bg-gradient-to-r from-fuchsia-50 to-violet-50 dark:border-fuchsia-700/50 dark:from-fuchsia-900/20 dark:to-violet-900/20",
    isTrainingGround &&
      "border border-cyan-200/70 bg-gradient-to-r from-cyan-50 to-emerald-50 dark:border-cyan-700/50 dark:from-cyan-900/20 dark:to-emerald-900/20",
    isActive && !isAccent
      ? "bg-primary/10 text-primary shadow-sm"
      : isActive && isLeaderboard
        ? "bg-gradient-to-r from-yellow-100 to-orange-100 text-yellow-700 shadow-md dark:from-yellow-800/30 dark:to-orange-800/30 dark:text-yellow-300"
        : isActive && isChallenges
          ? "bg-gradient-to-r from-fuchsia-100 to-violet-100 text-fuchsia-700 shadow-md dark:from-fuchsia-800/30 dark:to-violet-800/30 dark:text-fuchsia-300"
          : isActive && isTrainingGround
            ? "bg-gradient-to-r from-cyan-100 to-emerald-100 text-cyan-700 shadow-md dark:from-cyan-800/30 dark:to-emerald-800/30 dark:text-cyan-300"
            : !isAccent
              ? "text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white"
              : isLeaderboard
                ? "text-yellow-600 hover:scale-105 hover:bg-gradient-to-r hover:from-yellow-100 hover:to-orange-100 hover:text-yellow-700 hover:shadow-md dark:text-yellow-400 dark:hover:from-yellow-800/30 dark:hover:to-orange-800/30 dark:hover:text-yellow-300"
                : isTrainingGround
                  ? "text-cyan-600 hover:scale-105 hover:bg-gradient-to-r hover:from-cyan-100 hover:to-emerald-100 hover:text-cyan-700 hover:shadow-md dark:text-cyan-400 dark:hover:from-cyan-800/30 dark:hover:to-emerald-800/30 dark:hover:text-cyan-300"
                  : "text-fuchsia-600 hover:scale-105 hover:bg-gradient-to-r hover:from-fuchsia-100 hover:to-violet-100 hover:text-fuchsia-700 hover:shadow-md dark:text-fuchsia-400 dark:hover:from-fuchsia-800/30 dark:hover:to-violet-800/30 dark:hover:text-fuchsia-300",
  );
}

function NavItemContent({ item, isActive }: { item: NavItem; isActive: boolean }) {
  const Icon = item.icon;
  const isLeaderboard = item.to === URLS.leaderboard.topAssessments;
  const isChallenges = item.to === URLS.challenges.list;
  const isTrainingGround = item.to === URLS.trainingGround;
  const isAccent = isLeaderboard || isChallenges || isTrainingGround;

  return (
    <>
      {isLeaderboard && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-yellow-400/20 to-orange-500/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      )}
      {isChallenges && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-fuchsia-400/20 to-violet-500/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      )}
      {isTrainingGround && (
        <div className="absolute inset-0 rounded-lg bg-gradient-to-r from-cyan-400/20 to-emerald-500/20 opacity-0 transition-opacity duration-300 group-hover:opacity-100" />
      )}

      <Icon
        className={cn(
          "h-4 w-4 shrink-0 transition-transform duration-300",
          isAccent && "group-hover:scale-110",
          isActive && isAccent && "animate-pulse",
        )}
      />

      <span className="relative z-10 whitespace-nowrap">{item.label}</span>

      {isLeaderboard && (
        <div className="absolute -right-1 -top-1 h-2 w-2 animate-pulse rounded-full bg-gradient-to-r from-yellow-400 to-orange-500" />
      )}
      {isChallenges && (
        <div className="absolute -right-1 -top-1 h-2 w-2 animate-pulse rounded-full bg-gradient-to-r from-fuchsia-500 to-violet-500" />
      )}
      {isTrainingGround && (
        <div className="absolute -right-1 -top-1 h-2 w-2 animate-pulse rounded-full bg-gradient-to-r from-cyan-400 to-emerald-500" />
      )}
    </>
  );
}

function MoreButtonContent({ active }: { active: boolean }) {
  return (
    <>
      <span>{trans("moreNavigation")}</span>
      <ChevronDown
        className={cn("h-4 w-4 shrink-0", active && "text-primary")}
      />
    </>
  );
}

const moreButtonClassName = (active: boolean) =>
  cn(
    "flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-xl px-3 py-2.5 text-sm font-bold transition-colors duration-200",
    active
      ? "bg-primary/10 text-primary shadow-sm"
      : "text-slate-600 hover:bg-slate-100 hover:text-slate-950 dark:text-slate-300 dark:hover:bg-slate-800 dark:hover:text-white",
  );

/** Server / pre-measure default: this many links, the rest under "More". */
const DEFAULT_VISIBLE_COUNT = 5;
const GAP = 4; // gap-1

export function HeaderNavLinks() {
  const location = useLocation();
  const navigationLinks = useNavigationLinks();
  const homeLinks = useHomeNavigationLinks();
  const isLeaderBoardFeatureEnabled = useFeatureFlag("leaderboard");

  const items = useMemo(() => {
    const feedUrl = URLS.feed;
    const list: NavItem[] = [
      ...homeLinks.map(link => ({
        ...link,
        exact: link.to === URLS.home || link.to === "/",
      })),
      ...navigationLinks.filter(
        link =>
          !(
            link.to === URLS.leaderboard.topAssessments &&
            !isLeaderBoardFeatureEnabled
          ),
      ),
    ];

    // Stable sort by rank.
    return list
      .map((item, index) => ({ item, index, rank: getRank(item, feedUrl) }))
      .sort((a, b) => a.rank - b.rank || a.index - b.index)
      .map(entry => entry.item);
  }, [homeLinks, navigationLinks, isLeaderBoardFeatureEnabled]);

  const navRef = useRef<HTMLElement>(null);
  const measureRef = useRef<HTMLDivElement>(null);
  const [visibleCount, setVisibleCount] = useState(DEFAULT_VISIBLE_COUNT);

  const itemsKey = items.map(item => `${item.to}|${item.label}`).join(",");

  useIsoLayoutEffect(() => {
    const nav = navRef.current;
    const measure = measureRef.current;

    if (!nav || !measure) return;

    const compute = () => {
      const available = nav.clientWidth;

      if (available <= 0) return; // hidden (display: none), keep last value

      const children = Array.from(measure.children) as HTMLElement[];
      const widths = children.map(child => child.offsetWidth);
      const moreWidth = widths.pop() ?? 0;

      if (widths.length === 0 || widths.some(width => width === 0)) return;

      const total =
        widths.reduce((sum, width) => sum + width, 0) +
        GAP * (widths.length - 1);

      if (total <= available) {
        setVisibleCount(widths.length);
        return;
      }

      let used = moreWidth;
      let count = 0;

      for (const width of widths) {
        if (used + GAP + width > available) break;
        used += GAP + width;
        count++;
      }

      setVisibleCount(Math.max(1, count));
    };

    compute();

    const observer = new ResizeObserver(compute);
    observer.observe(nav);

    // Fonts / translations can change label widths after mount.
    const fonts = (document as Document & { fonts?: FontFaceSet }).fonts;
    fonts?.ready.then(compute).catch(() => undefined);

    return () => observer.disconnect();
  }, [itemsKey]);

  const count = Math.min(visibleCount, items.length);
  const visible = items.slice(0, count);
  const overflow = items.slice(count);
  const isOverflowActive = overflow.some(item =>
    isItemActive(item, location.pathname),
  );

  return (
    <nav
      ref={navRef}
      className="relative hidden min-w-0 flex-1 items-center gap-1 lg:flex">
      {visible.map(item => {
        const isActive = isItemActive(item, location.pathname);

        return (
          <Link
            key={item.to}
            to={item.to}
            aria-current={isActive ? "page" : undefined}
            className={getItemClassName(item.to, isActive)}>
            <NavItemContent item={item} isActive={isActive} />
          </Link>
        );
      })}

      {overflow.length > 0 && (
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              type="button"
              className={moreButtonClassName(isOverflowActive)}
              aria-label={plainTrans("moreNavigation")}>
              <MoreButtonContent active={isOverflowActive} />
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent align="start" className="min-w-48">
            {overflow.map(item => {
              const Icon = item.icon;
              const isActive = isItemActive(item, location.pathname);

              return (
                <DropdownMenuItem key={item.to} asChild>
                  <Link
                    to={item.to}
                    aria-current={isActive ? "page" : undefined}
                    className={cn(
                      "flex w-full items-center gap-2 whitespace-nowrap font-semibold",
                      isActive && "bg-primary/10 text-primary",
                    )}>
                    <Icon className="h-4 w-4 shrink-0" />
                    <span>{item.label}</span>
                  </Link>
                </DropdownMenuItem>
              );
            })}
          </DropdownMenuContent>
        </DropdownMenu>
      )}

      {/* Invisible measuring row: natural width of every link + the More button */}
      <div
        ref={measureRef}
        aria-hidden="true"
        className="pointer-events-none invisible absolute start-0 top-0 -z-10 flex h-0 gap-1 overflow-hidden">
        {items.map(item => (
          <span key={item.to} className={getItemClassName(item.to, false)}>
            <item.icon className="h-4 w-4 shrink-0" />
            <span>{item.label}</span>
          </span>
        ))}
        <span className={moreButtonClassName(false)}>
          <MoreButtonContent active={false} />
        </span>
      </div>
    </nav>
  );
}
